(() => {
  "use strict";

  const SESSION_KEY = "ge-winter-seen";
  const SIGNUP_KEY = "ge-winter-signup";
  const instances = new WeakMap();

  function qs(root, name) {
    return root.querySelector(`[data-ge-winter-${name}]`);
  }

  class GeWinterPopup {
    constructor(root) {
      this.root = root;
      this.canvas = qs(root, "fog");
      this.ctx = this.canvas.getContext("2d", { willReadFrequently: true });
      this.intro = qs(root, "intro");
      this.reward = qs(root, "reward");
      this.copyBtn = qs(root, "copy");
      this.form = qs(root, "form");
      this.email = qs(root, "email");
      this.submitBtn = qs(root, "submit");
      this.unlock = qs(root, "unlock");
      this.status = qs(root, "status");
      this.alternative = qs(root, "alternative");
      this.footerMessage = qs(root, "footer-message");
      this.closeBtn = qs(root, "close");
      this.coupon = root.dataset.coupon || "RESET10";
      this.delay = Number(root.dataset.delay || 600);
      this.oncePerSession = root.dataset.once !== "false";
      this.w = 0;
      this.h = 0;
      this.dpr = 1;
      this.down = false;
      this.last = null;
      this.revealed = false;
      this.strokes = [];
      this.checks = 0;
      this.hasWiped = false;
      this.resizeTimer = null;
      this.steamTimer = null;
      this.openTimer = null;
      this.iceGrowth = null;
      this.lastActivity = performance.now();
      this.fogTexture = document.createElement("canvas");
      this.fogTextureCtx = this.fogTexture.getContext("2d");
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.prevFocus = null;
      this.prevOverflow = "";
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundPointerDown = this.onPointerDown.bind(this);
      this.boundPointerMove = this.onPointerMove.bind(this);
      this.boundPointerUp = this.onPointerUp.bind(this);
      this.boundPointerCancel = this.onPointerCancel.bind(this);
      this.boundResize = this.onResize.bind(this);
      this.boundActivity = this.markActivity.bind(this);
      this.boundControlKey = this.onControlKey.bind(this);
      this.bind();
    }

    bind() {
      this.canvas.addEventListener("pointerdown", this.boundPointerDown);
      this.canvas.addEventListener("pointermove", this.boundPointerMove);
      this.canvas.addEventListener("pointerup", this.boundPointerUp);
      this.canvas.addEventListener("pointercancel", this.boundPointerCancel);
      this.alternative.addEventListener("click", () => {
        if (this.revealed) this.reset();
        else this.reveal();
      });
      this.copyBtn.addEventListener("click", () => this.copyCode());
      if (this.form) this.form.addEventListener("submit", (e) => this.onSignup(e));
      this.closeBtn.addEventListener("click", () => this.close());
      this.root.addEventListener("pointermove", this.boundActivity, { passive: true });
      this.root.addEventListener("pointerdown", this.boundActivity, { passive: true });
      this.root.addEventListener("keydown", this.boundControlKey);
      window.addEventListener("resize", this.boundResize);
    }

    destroy() {
      clearTimeout(this.resizeTimer);
      clearTimeout(this.openTimer);
      clearInterval(this.steamTimer);
      this.steamTimer = null;
      if (this.boundSteamVis) document.removeEventListener("visibilitychange", this.boundSteamVis);
      this.iceGrowth = null;
      this.canvas.removeEventListener("pointerdown", this.boundPointerDown);
      this.canvas.removeEventListener("pointermove", this.boundPointerMove);
      this.canvas.removeEventListener("pointerup", this.boundPointerUp);
      this.canvas.removeEventListener("pointercancel", this.boundPointerCancel);
      this.root.removeEventListener("pointermove", this.boundActivity);
      this.root.removeEventListener("pointerdown", this.boundActivity);
      this.root.removeEventListener("keydown", this.boundControlKey);
      document.removeEventListener("keydown", this.boundKeydown);
      window.removeEventListener("resize", this.boundResize);
      this.unlockPage();
    }

    scheduleOpen() {
      const design = this.root.dataset.designMode === "true";
      if (!design && this.oncePerSession) {
        try {
          if (sessionStorage.getItem(SESSION_KEY)) return;
        } catch (e) {
          /* private mode */
        }
      }
      this.openTimer = setTimeout(() => this.open(), this.delay);
    }

    open() {
      this.prevFocus = document.activeElement;
      this.root.hidden = false;
      this.root.setAttribute("aria-hidden", "false");
      this.lockPage();
      document.addEventListener("keydown", this.boundKeydown);
      this.reset();
      this.closeBtn.focus();
      this.startSteam();
      if (!this.boundSteamVis) {
        this.boundSteamVis = () => (document.hidden ? this.stopSteam() : this.startSteam());
        document.addEventListener("visibilitychange", this.boundSteamVis);
      }
    }

    close() {
      if (this.oncePerSession) {
        try {
          sessionStorage.setItem(SESSION_KEY, "1");
        } catch (e) {
          /* private mode */
        }
      }
      this.root.hidden = true;
      this.root.setAttribute("aria-hidden", "true");
      document.removeEventListener("keydown", this.boundKeydown);
      clearInterval(this.steamTimer);
      this.steamTimer = null;
      this.iceGrowth = null;
      this.unlockPage();
      if (this.prevFocus && typeof this.prevFocus.focus === "function") {
        this.prevFocus.focus();
      }
    }

    lockPage() {
      // Lock on <html>: body overflow:hidden breaks the sticky header (jumps by announcement height).
      this.prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = "hidden";
    }

    unlockPage() {
      document.documentElement.style.overflow = this.prevOverflow;
    }

    onKeydown(event) {
      if (this.root.hidden) return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== "Tab") return;
      const nodes = this.focusables();
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    focusables() {
      return Array.from(
        this.root.querySelectorAll("button:not([tabindex='-1']), a[href], [tabindex]:not([tabindex='-1'])")
      ).filter((el) => !el.disabled && !el.hidden && el.offsetParent !== null);
    }

    markActivity() {
      this.lastActivity = performance.now();
      this.iceGrowth = null;
    }

    onControlKey() {
      this.lastActivity = performance.now();
      this.iceGrowth = null;
    }

    randomFactory() {
      let seed = 42;
      return () => {
        seed = (seed * 16807) % 2147483647;
        return (seed - 1) / 2147483646;
      };
    }

    drawFog() {
      const box = this.root.getBoundingClientRect();
      if (!box.width || !box.height) return;
      this.w = box.width;
      this.h = box.height;
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(this.w * this.dpr);
      this.canvas.height = Math.round(this.h * this.dpr);
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      this.ctx.globalCompositeOperation = "source-over";
      const g = this.ctx.createLinearGradient(0, 0, this.w, this.h);
      g.addColorStop(0, "#a3c4cc");
      g.addColorStop(0.22, "#77a9bb");
      g.addColorStop(0.55, "#3a7896");
      g.addColorStop(1, "#193c57");
      this.ctx.fillStyle = g;
      this.ctx.fillRect(0, 0, this.w, this.h);
      const glow = this.ctx.createRadialGradient(
        this.w * 0.68,
        this.h * 0.05,
        0,
        this.w * 0.68,
        this.h * 0.05,
        Math.max(this.w, this.h) * 0.85
      );
      glow.addColorStop(0, "rgba(167,236,247,.48)");
      glow.addColorStop(0.45, "rgba(64,160,192,.13)");
      glow.addColorStop(1, "rgba(21,46,68,0)");
      this.ctx.fillStyle = glow;
      this.ctx.fillRect(0, 0, this.w, this.h);
      const random = this.randomFactory();
      for (let i = 0; i < 95; i++) {
        const x = random() * this.w;
        const y = random() * this.h;
        const r = 25 + random() * Math.min(this.w, this.h) * 0.26;
        const cloud = this.ctx.createRadialGradient(x, y, 0, x, y, r);
        cloud.addColorStop(0, "rgba(210,244,249," + (0.02 + random() * 0.08) + ")");
        cloud.addColorStop(1, "rgba(210,244,249,0)");
        this.ctx.fillStyle = cloud;
        this.ctx.fillRect(x - r, y - r, r * 2, r * 2);
      }
      const grains = Math.min(38000, Math.round((this.w * this.h) / 22));
      for (let i = 0; i < grains; i++) {
        this.ctx.fillStyle = "rgba(225,250,255," + (0.025 + random() * 0.18) + ")";
        const size = 0.3 + random() * 1.3;
        this.ctx.fillRect(random() * this.w, random() * this.h, size, size);
      }
      const scale = Math.min(this.w, this.h);
      for (let i = 0; i < 140; i++) {
        const edge = Math.floor(random() * 4);
        const depth = Math.pow(random(), 2) * scale * 0.23;
        let x;
        let y;
        if (edge === 0) {
          x = depth;
          y = random() * this.h;
        } else if (edge === 1) {
          x = this.w - depth;
          y = random() * this.h;
        } else if (edge === 2) {
          x = random() * this.w;
          y = depth;
        } else {
          x = random() * this.w;
          y = this.h - depth;
        }
        const radius = 8 + random() * scale * 0.075;
        const powder = this.ctx.createRadialGradient(x, y, 0, x, y, radius);
        powder.addColorStop(0, "rgba(233,251,255," + (0.05 + random() * 0.17) + ")");
        powder.addColorStop(0.5, "rgba(216,241,249,.045)");
        powder.addColorStop(1, "rgba(216,241,249,0)");
        this.ctx.fillStyle = powder;
        this.ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
      }
      for (let i = 0; i < 3200; i++) {
        const edge = Math.floor(random() * 4);
        const depth = Math.pow(random(), 2.4) * scale * 0.2;
        let x;
        let y;
        if (edge === 0) {
          x = depth;
          y = random() * this.h;
        } else if (edge === 1) {
          x = this.w - depth;
          y = random() * this.h;
        } else if (edge === 2) {
          x = random() * this.w;
          y = depth;
        } else {
          x = random() * this.w;
          y = this.h - depth;
        }
        const radius = 0.25 + random() * 1.6;
        this.ctx.fillStyle = "rgba(241,253,255," + (0.08 + random() * 0.36) + ")";
        this.ctx.beginPath();
        this.ctx.arc(x, y, radius, 0, Math.PI * 2);
        this.ctx.fill();
      }
      this.fogTexture.width = this.canvas.width;
      this.fogTexture.height = this.canvas.height;
      this.fogTextureCtx.drawImage(this.canvas, 0, 0);
      if (!this.iceGrowth) {
        for (const s of this.strokes) {
          this.erase({ x: s.a.x * this.w, y: s.a.y * this.h }, { x: s.b.x * this.w, y: s.b.y * this.h });
        }
      }
      this.root.classList.add("is-fog-ready");
    }

    erase(a, b) {
      const ctx = this.ctx;
      ctx.globalCompositeOperation = "destination-out";
      ctx.strokeStyle = "rgba(0, 0, 0, 1)";
      ctx.lineWidth = Math.max(56, Math.min(this.w, this.h) * 0.14);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.shadowColor = "rgba(0, 0, 0, .9)";
      ctx.shadowBlur = 7;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    point(event) {
      const r = this.canvas.getBoundingClientRect();
      return { x: event.clientX - r.left, y: event.clientY - r.top };
    }

    onPointerDown(event) {
      if (event.button !== 0) return;
      this.down = true;
      this.last = this.point(event);
      this.canvas.setPointerCapture(event.pointerId);
      this.wipe(event);
    }

    onPointerMove(event) {
      this.wipe(event);
    }

    onPointerUp() {
      this.down = false;
      this.last = null;
      this.measure();
    }

    onPointerCancel() {
      this.down = false;
      this.last = null;
    }

    wipe(event) {
      if (!this.down || this.revealed) return;
      this.iceGrowth = null;
      this.lastActivity = performance.now();
      const p = this.point(event);
      const a = this.last || p;
      this.erase(a, p);
      this.strokes.push({
        a: { x: a.x / this.w, y: a.y / this.h },
        b: { x: p.x / this.w, y: p.y / this.h },
      });
      this.last = p;
      if (!this.hasWiped) {
        this.hasWiped = true;
        this.intro.classList.add("is-hidden");
        this.footerMessage.textContent = "Keep going. Your cold-weather treat is underneath.";
      }
      if (++this.checks % 8 === 0) this.measure();
    }

    clearedFraction(x, y, width, height) {
      const pixels = this.ctx.getImageData(x, y, width, height).data;
      let total = 0;
      let clear = 0;
      for (let i = 3; i < pixels.length; i += 64) {
        total++;
        if (pixels[i] < 80) clear++;
      }
      return total ? clear / total : 0;
    }

    measure() {
      if (this.revealed || !this.hasWiped || this.down) return;
      const rect = this.reward.getBoundingClientRect();
      const bounds = this.canvas.getBoundingClientRect();
      const x = Math.max(0, Math.floor((rect.left - bounds.left) * this.dpr));
      const y = Math.max(0, Math.floor((rect.top - bounds.top) * this.dpr));
      const rw = Math.min(this.canvas.width - x, Math.ceil(rect.width * this.dpr));
      const rh = Math.min(this.canvas.height - y, Math.ceil(rect.height * this.dpr));
      if (rw <= 0 || rh <= 0) return;
      const screenCoverage = this.clearedFraction(0, 0, this.canvas.width, this.canvas.height);
      const offerCoverage = this.clearedFraction(x, y, rw, rh);
      if (screenCoverage >= 0.28 && offerCoverage >= 0.78) this.reveal();
    }

    reveal() {
      if (this.revealed) return;
      this.iceGrowth = null;
      this.canvas.classList.remove("is-crystallizing");
      this.lastActivity = performance.now();
      this.hasWiped = true;
      this.revealed = true;
      this.down = false;
      this.intro.classList.add("is-hidden");
      this.canvas.classList.add("is-cleared");
      this.reward.classList.add("is-revealed");
      this.reward.removeAttribute("aria-hidden");
      this.copyBtn.removeAttribute("tabindex");
      if (this.email) this.email.removeAttribute("tabindex");
      if (this.submitBtn) this.submitBtn.removeAttribute("tabindex");
      this.alternative.textContent = "Freeze it over again";
      this.footerMessage.textContent = "Your winter reset awaits.";
    }

    reset() {
      this.iceGrowth = null;
      this.canvas.classList.remove("is-crystallizing");
      this.lastActivity = performance.now();
      this.revealed = false;
      this.down = false;
      this.last = null;
      this.strokes = [];
      this.checks = 0;
      this.hasWiped = false;
      this.intro.classList.remove("is-hidden");
      this.canvas.classList.remove("is-cleared");
      this.reward.classList.remove("is-revealed");
      this.reward.setAttribute("aria-hidden", "true");
      this.copyBtn.tabIndex = -1;
      this.copyBtn.textContent = "Copy your code";
      this.status.textContent = "";
      this.alternative.textContent = "Reveal without wiping";
      this.footerMessage.textContent = "A little calm for colder days.";
      this.drawFog();
    }

    beginRefog() {
      this.revealed = false;
      this.down = false;
      this.last = null;
      this.strokes = [];
      this.checks = 0;
      this.ctx.save();
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.restore();
      this.reward.classList.remove("is-revealed");
      this.reward.setAttribute("aria-hidden", "true");
      this.copyBtn.tabIndex = -1;
      this.copyBtn.textContent = "Copy your code";
      this.status.textContent = "";
      if (document.activeElement === this.copyBtn) this.alternative.focus();
      this.canvas.classList.add("is-crystallizing");
      this.canvas.classList.remove("is-cleared");
      this.alternative.textContent = "Reveal without wiping";
    }

    iceNoise(x, y) {
      const hash = (a, b) => {
        const n = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
        return n - Math.floor(n);
      };
      const ix = Math.floor(x);
      const iy = Math.floor(y);
      let fx = x - ix;
      let fy = y - iy;
      fx = fx * fx * (3 - 2 * fx);
      fy = fy * fy * (3 - 2 * fy);
      return (
        (hash(ix, iy) * (1 - fx) + hash(ix + 1, iy) * fx) * (1 - fy) +
        (hash(ix, iy + 1) * (1 - fx) + hash(ix + 1, iy + 1) * fx) * fy
      );
    }

    buildIceField(width, height) {
      const mw = Math.ceil(width / 3);
      const mh = Math.ceil(height / 3);
      const field = new Float32Array(mw * mh);
      const scale = Math.min(width, height);
      const seeds = [
        { x: width * 0.18, y: height * 0.74 },
        { x: width * 0.82, y: height * 0.28 },
      ];
      let lo = Infinity;
      let hi = -Infinity;
      for (let py = 0; py < mh; py++) {
        for (let px = 0; px < mw; px++) {
          const x = (px / mw) * width;
          const y = (py / mh) * height;
          const broad = this.iceNoise(x / 95, y / 95);
          const medium = this.iceNoise(x / 35 + 17, y / 35 + 9);
          const fine = this.iceNoise(x / 12 + 4, y / 12 + 21);
          const warpX = x + (broad - 0.5) * scale * 0.22;
          const warpY = y + (medium - 0.5) * scale * 0.13;
          let distance = Math.min(x, width - x, y * 0.8, (height - y) * 0.8);
          for (const seed of seeds) {
            distance = Math.min(distance, Math.hypot(warpX - seed.x, (warpY - seed.y) * 0.85) + scale * 0.1);
          }
          const value = distance / scale + (broad - 0.5) * 0.2 + (medium - 0.5) * 0.085 + (fine - 0.5) * 0.025;
          field[py * mw + px] = value;
          lo = Math.min(lo, value);
          hi = Math.max(hi, value);
        }
      }
      for (let i = 0; i < field.length; i++) field[i] = 0.02 + ((field[i] - lo) / (hi - lo)) * 0.94;
      return { field, mw, mh };
    }

    createIceGrowth(now) {
      const data = this.buildIceField(this.w, this.h);
      const mask = document.createElement("canvas");
      const layer = document.createElement("canvas");
      mask.width = data.mw;
      mask.height = data.mh;
      layer.width = this.canvas.width;
      layer.height = this.canvas.height;
      const maskCtx = mask.getContext("2d");
      const layerCtx = layer.getContext("2d");
      this.canvas.classList.add("is-crystallizing");
      return {
        ...data,
        mask,
        layer,
        maskCtx,
        layerCtx,
        pixels: maskCtx.createImageData(data.mw, data.mh),
        start: now,
      };
    }

    drawOrganicIce(growth, progress) {
      const pixels = growth.pixels.data;
      for (let i = 0; i < growth.field.length; i++) {
        let coverage = Math.max(0, Math.min(1, (progress - growth.field[i]) / 0.025));
        coverage = coverage * coverage * (3 - 2 * coverage);
        const p = i * 4;
        pixels[p] = 255;
        pixels[p + 1] = 255;
        pixels[p + 2] = 255;
        pixels[p + 3] = Math.round(coverage * 255);
      }
      growth.maskCtx.putImageData(growth.pixels, 0, 0);
      const layerCtx = growth.layerCtx;
      layerCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      layerCtx.globalCompositeOperation = "source-over";
      layerCtx.drawImage(this.fogTexture, 0, 0);
      layerCtx.globalCompositeOperation = "destination-in";
      layerCtx.imageSmoothingEnabled = true;
      layerCtx.drawImage(growth.mask, 0, 0, this.canvas.width, this.canvas.height);
      this.ctx.save();
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.ctx.globalCompositeOperation = "source-over";
      this.ctx.globalAlpha = 1;
      this.ctx.shadowBlur = 0;
      this.ctx.drawImage(growth.layer, 0, 0);
      this.ctx.restore();
    }

    finishIceReset() {
      this.ctx.save();
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.ctx.globalCompositeOperation = "source-over";
      this.ctx.globalAlpha = 1;
      this.ctx.drawImage(this.fogTexture, 0, 0);
      this.ctx.restore();
      this.iceGrowth = null;
      this.revealed = false;
      this.down = false;
      this.last = null;
      this.strokes = [];
      this.checks = 0;
      this.hasWiped = false;
      this.lastActivity = performance.now();
      this.reward.classList.remove("is-revealed");
      this.reward.setAttribute("aria-hidden", "true");
      this.copyBtn.tabIndex = -1;
      this.copyBtn.textContent = "Copy your code";
      this.status.textContent = "";
      this.canvas.classList.remove("is-cleared");
      this.alternative.textContent = "Reveal without wiping";
      this.footerMessage.textContent = "A little calm for colder days.";
      this.intro.classList.remove("is-hidden");
    }

    startSteam() {
      if (this.steamTimer || this.root.hidden || document.hidden) return;
      this.steamTimer = setInterval(() => this.steamTick(), 180);
    }

    stopSteam() {
      clearInterval(this.steamTimer);
      this.steamTimer = null;
      this.steamTimer = null;
    }

    steamTick() {
      if (document.hidden || this.root.hidden || this.down || !this.hasWiped) return;
      if (this.isUsingControl()) return;
      const now = performance.now();
      const idle = now - this.lastActivity;
      if (idle < 4500) return;
      if (this.revealed) this.beginRefog();
      if (!this.iceGrowth) this.iceGrowth = this.createIceGrowth(now);
      const progress = Math.min(1, (now - this.iceGrowth.start) / 9000);
      this.drawOrganicIce(this.iceGrowth, progress);
      if (idle > 5500) this.footerMessage.textContent = "The glass is freezing over. Wipe to uncover your treat.";
      if (progress >= 1) this.finishIceReset();
    }

    isUsingControl() {
      const active = document.activeElement;
      return active === this.copyBtn || active === this.email || active === this.submitBtn || active === this.alternative || active === this.closeBtn;
    }

    onSignup(event) {
      this.lastActivity = performance.now();
      const value = (this.email.value || "").trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        event.preventDefault();
        this.status.textContent = "Please enter a valid email address.";
        this.email.focus();
        return;
      }
      // Let the native post go through so Shopify's hCaptcha can attach its token.
      // A fetch() post has no token and Shopify rejects it ("Missing CAPTCHA token").
      try {
        sessionStorage.setItem(SIGNUP_KEY, "1");
      } catch (e) {
        /* private mode */
      }
      this.status.textContent = "Signing you up…";
    }

    showSignedUp() {
      this.open();
      this.reveal();
      if (this.form) this.form.hidden = true;
      this.unlock.hidden = false;
      this.status.textContent = "You are signed up. Here is your code.";
      this.copyBtn.focus();
    }

    showSignupError(detail) {
      this.open();
      this.reveal();
      const d = (detail || "").toLowerCase();
      let msg = "We couldn't sign you up just now. Please try again.";
      if (/captcha|challenge|robot/.test(d)) msg = "Shopify needs a quick human check. Tap unlock my code again.";
      else if (/email/.test(d) && /invalid|valid/.test(d)) msg = "That email doesn't look right. Please check it and try again.";
      else if (detail) msg = "We couldn't sign you up: " + detail.replace(/\s+/g, " ").trim();
      this.status.textContent = msg;
      if (this.email) this.email.focus();
    }

    async copyCode() {
      this.lastActivity = performance.now();
      try {
        await navigator.clipboard.writeText(this.coupon);
        this.copyBtn.textContent = "Code copied";
        this.status.textContent = this.root.dataset.copySuccess || "Sample code copied. A little good for colder days.";
      } catch (e) {
        this.status.textContent = "Your sample code: " + this.coupon;
      }
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        if (this.iceGrowth) {
          this.iceGrowth = null;
          this.strokes = [];
          this.canvas.classList.remove("is-crystallizing");
        }
        this.drawFog();
      }, 150);
    }
  }

  function mount(root) {
    if (!root || instances.has(root)) return;
    const popup = new GeWinterPopup(root);
    instances.set(root, popup);
    let pending = false;
    try {
      pending = sessionStorage.getItem(SIGNUP_KEY) === "1";
      sessionStorage.removeItem(SIGNUP_KEY);
    } catch (e) {
      /* private mode */
    }
    const result = root.querySelector("[data-ge-winter-result]");
    const posted = result && result.dataset.geWinterResult;
    const detail = (result && result.dataset.geWinterError) || "";
    const urlOk = /[?&]customer_posted=true/.test(location.search);
    const alreadyIn = /taken|subscribed|already|exist/i.test(detail);
    // Any posted state (marker or Shopify's own result) resolves the popup: code or a specific message.
    if (posted === "ok" || urlOk || alreadyIn) {
      if (pending || urlOk || posted) popup.showSignedUp();
      else popup.scheduleOpen();
    } else if (posted === "error") popup.showSignupError(detail);
    else popup.scheduleOpen();
  }

  function unmount(root) {
    const popup = instances.get(root);
    if (!popup) return;
    popup.destroy();
    instances.delete(root);
  }

  function boot() {
    document.querySelectorAll("[data-ge-winter]").forEach(mount);
  }

  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target.querySelector("[data-ge-winter]");
    if (root) mount(root);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target.querySelector("[data-ge-winter]");
    if (root) unmount(root);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
